import React, { useCallback, useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { fetchKnives, addKnife, deleteKnife, uploadToImgur } from './api';
import Header from './Header';
import FilmStrip from './FilmStrip';

const swalDark = {
  background: '#171310',
  color: '#f0e9df',
  confirmButtonColor: '#e13a2e',
  cancelButtonColor: '#3a2f26',
};

function formatPrice(price) {
  const num = Number(price);
  if (!isNaN(num) && price !== '' && price !== null) {
    return `${num.toLocaleString('cs-CZ')} Kč`;
  }
  return price;
}

export default function Knives({ onBack, onLogout }) {
  const [knives, setKnives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [imgurLink, setImgurLink] = useState(null);
  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const loadKnives = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchKnives();
      if (!data.success) throw new Error(data.message || 'Načtení se nezdařilo.');
      setKnives(data.knives || []);
    } catch (err) {
      setLoadError(err.message || 'Nepodařilo se spojit se serverem.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKnives();
  }, [loadKnives]);

  const resetForm = () => {
    setImgurLink(null);
    setPrice('');
    setDescription('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setImgurLink(null);
    try {
      const link = await uploadToImgur(file);
      setImgurLink(link);
    } catch (err) {
      Swal.fire({
        ...swalDark,
        title: 'Nahrání na Imgur selhalo',
        text: err.message || 'Zkus to prosím znovu.',
        icon: 'error',
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!imgurLink) return;

    setSaving(true);
    try {
      const res = await addKnife({ imageUrl: imgurLink, price, description });
      if (!res.success) throw new Error(res.message || 'Uložení se nezdařilo.');
      resetForm();
      await loadKnives();
      Swal.fire({
        ...swalDark,
        title: 'Přidáno do nabídky',
        icon: 'success',
        timer: 1300,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        ...swalDark,
        title: 'Chyba',
        text: err.message || 'Uložení se nezdařilo.',
        icon: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (knife) => {
    const result = await Swal.fire({
      ...swalDark,
      title: 'Smazat nůž z nabídky?',
      text: 'Tuto akci nelze vrátit zpět.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Smazat',
      cancelButtonText: 'Zrušit',
      reverseButtons: true,
      focusCancel: true,
    });

    if (!result.isConfirmed) return;

    try {
      const res = await deleteKnife(knife.id);
      if (!res.success) throw new Error(res.message || 'Smazání se nezdařilo.');
      setKnives((prev) => prev.filter((k) => k.id !== knife.id));
    } catch (err) {
      Swal.fire({
        ...swalDark,
        title: 'Chyba',
        text: err.message || 'Smazání se nezdařilo.',
        icon: 'error',
      });
    }
  };

  return (
    <div className="page">
      <Header eyebrow="Kontaktní arch" title="Volné nože" onBack={onBack} onLogout={onLogout} />
      <FilmStrip />

      <main className="main">
        <form className="add-form" onSubmit={handleSave}>
          <p className="add-form__title">Přidat nový nůž do nabídky</p>

          {!imgurLink && (
            <label className={`add-form__photo-picker ${uploading ? 'is-busy' : ''}`}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePickPhoto}
                disabled={uploading}
                className="dropzone__input"
              />
              {uploading ? 'Nahrávám fotku na Imgur…' : '1. Vybrat fotku nože'}
            </label>
          )}

          {imgurLink && (
            <>
              <div className="add-form__preview">
                <img src={imgurLink} alt="Náhled" />
                <button
                  type="button"
                  className="add-form__preview-remove"
                  onClick={resetForm}
                  disabled={saving}
                >
                  Vybrat jinou fotku
                </button>
              </div>

              <input
                type="number"
                inputMode="numeric"
                className="add-form__input"
                placeholder="Cena v Kč"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                disabled={saving}
                required
              />
              <textarea
                className="add-form__input add-form__textarea"
                placeholder="Krátký popis nože"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={saving}
                rows={3}
              />
              <button className="btn add-form__submit" type="submit" disabled={saving}>
                {saving ? 'Ukládám…' : 'Přidat do nabídky'}
              </button>
            </>
          )}
        </form>

        {loadError && (
          <div className="state state--error">
            <p>{loadError}</p>
            <button className="btn btn--ghost" onClick={loadKnives}>Zkusit znovu</button>
          </div>
        )}

        {!loadError && loading && (
          <div className="state">
            <p>Načítám nabídku…</p>
          </div>
        )}

        {!loadError && !loading && knives.length === 0 && (
          <div className="state">
            <p>V nabídce zatím nejsou žádné nože.</p>
            <p className="state__sub">Přidej první kus výše.</p>
          </div>
        )}

        {!loadError && !loading && knives.length > 0 && (
          <div className="grid">
            {knives.map((knife) => (
              <article className="frame" key={knife.id}>
                <div className="frame__photo">
                  <img src={knife.image_url} alt={knife.description || 'Nůž'} loading="lazy" />
                </div>
                <div className="frame__meta">
                  <p className="frame__price">{formatPrice(knife.price)}</p>
                  {knife.description && (
                    <p className="frame__sub frame__sub--wrap">{knife.description}</p>
                  )}
                </div>
                <button className="frame__delete" onClick={() => handleDelete(knife)}>
                  Smazat
                </button>
              </article>
            ))}
          </div>
        )}
      </main>

      <footer className="footer">
        <span className="footer__dot" /> Připojeno k backendu
      </footer>
    </div>
  );
}
